import { AccountInfo, AuthError, InteractionRequiredAuthError, InteractionStatus, InteractionType, RedirectRequest } from "@azure/msal-browser";
import { useEffect, useMemo } from "react";
import { useMsal, useMsalAuthentication } from "@azure/msal-react";

import { LogCategory, RenderingStatus } from "Enumerations";
import { runInAction } from "mobx";
import { useAppSettings, useLogging } from "Shared/Hooks";
import { useAuthScopes } from "./useAuthScopes";
import { useGlobalSetting } from "Store/GlobalSettingStore";
import { useRootStore } from "Store/RootStore";
import { useNavigate, useLocation } from "react-router";
import { newGuid } from "Shared/Utils";

const loginCacheActionKey = "loginCacheAction";

export const useLogin = (torusRequired?: boolean) => {
    const authStore = useRootStore("AuthStore");
    const myStore = useRootStore("MyStore");
    const { accounts, inProgress, instance } = useMsal();
    const { torusTenantId, microsoftTenantId } = useAppSettings();
    const scopes = useAuthScopes();
    const navigate = useNavigate();
    const location = useLocation();
    const { trackTrace } = useLogging(LogCategory.Auth);

    const accountHint = useAccountHint(torusRequired);
    const forceInteractiveLogin = authStore.forceInteractiveLogin;

    const request: RedirectRequest = {
        scopes: scopes
    };
    const { result, error, login, acquireToken } = useMsalAuthentication(InteractionType.Silent, request, forceInteractiveLogin ? undefined : accountHint);

    useEffect(() => {
        if (forceInteractiveLogin) {
            authStore.clearInteractiveLoginRequest();
            (async () => {
                try {
                    await instance.loginRedirect({
                        scopes: scopes,
                        prompt: "select_account"
                    });
                } catch (redirectError: unknown) {
                    const authError = redirectError instanceof AuthError ? redirectError : undefined;
                    authStore.setLoginStatus(RenderingStatus.Error, authError);
                }
            })();
            return;
        }
        if (authStore.loginStatus === RenderingStatus.Waiting && inProgress === InteractionStatus.None) {
            if (error instanceof InteractionRequiredAuthError) {
                const guid = newGuid();
                trackTrace(guid, "Login error", { error: error.errorMessage });

                if (localStorage.getItem(loginCacheActionKey) === "login") {
                    accounts.forEach((account, index) => trackTrace(guid, `account ${index}`, { account: account.username }));
                    trackTrace(guid, "account hint", { accountHint: accountHint?.username ?? "no account hint" });
                    navigate(`/login?error=${guid}`);
                } else {
                    localStorage.setItem(loginCacheActionKey, "login");
                    login(InteractionType.Redirect, {
                        scopes: scopes,
                        prompt: "select_account"
                    });
                }
                return;
            }

            if (error) {
                authStore.setLoginStatus(RenderingStatus.Error, error);
            }

            if (result?.account.username) {
                instance.setActiveAccount(result.account);
                localStorage.removeItem(loginCacheActionKey);

                runInAction(() => {
                    authStore.readyToCheckRole = true;
                    authStore.login = login;
                    authStore.getToken = async () => {
                        const account = instance.getActiveAccount() ?? result.account;
                        const tokenResult = await instance.acquireTokenSilent({
                            scopes,
                            account
                        });
                        return tokenResult.accessToken;
                    };
                    if (result.account.tenantId.toLowerCase() === torusTenantId?.toLowerCase()) {
                        myStore.tenant = "Torus";
                    } else if (result.account.tenantId.toLowerCase() === microsoftTenantId?.toLowerCase()) {
                        myStore.tenant = "Microsoft";
                    }
                });
            }
        }
    }, [
        accounts,
        authStore.loginStatus,
        error,
        forceInteractiveLogin,
        inProgress,
        instance,
        result?.account.username,
        acquireToken,
        login,
        location.hash,
        location.pathname,
        location.search,
        scopes,
        authStore,
        result?.account,
        trackTrace,
        accountHint?.username,
        navigate,
        torusTenantId,
        microsoftTenantId,
        myStore
    ]);

    useEffect(() => {
        if (torusRequired && result) {
            authStore.SetTorusRequirementMet(result.account.tenantId === torusTenantId?.toLowerCase());
        }
    }, [authStore, result, torusRequired, torusTenantId]);

    return {
        loginStatus: authStore.loginStatus
    };
};

function useAccountHint(torusRequired: boolean | undefined) {
    const { accounts } = useMsal();
    const { torusTenantId } = useAppSettings();
    const { lastLoginUser, tenantId } = useGlobalSetting("auth");

    return useMemo(() => {
        let acc: AccountInfo | undefined;
        if (lastLoginUser) {
            const targetTenantId = torusRequired && torusTenantId ? torusTenantId : lastLoginUser.tenantId;
            acc = accounts.find((account) => account.tenantId.toLowerCase() === targetTenantId.toLowerCase());
        } else {
            acc = accounts.find((account) => account.tenantId === (torusRequired ? torusTenantId : tenantId));
        }
        if (!acc && accounts.length > 0) {
            acc = accounts[accounts.length - 1];
        }

        return acc;
    }, [accounts, lastLoginUser, tenantId, torusRequired, torusTenantId]);
}
